import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as htmlToImage from 'html-to-image';
import download from 'downloadjs';

interface InvoiceItem {
  name: string;
  quantity: number;
  price: number; // Price in USD
  description?: string;
  code?: string;
  size?: string;
  measurement?: string;
  length?: string;
  width?: string;
  material?: string;
  quality?: string;
  usageTime?: string;
  usageLimit?: number;
  brand?: string;
  origin?: string;
  condition?: string;
}

interface InvoiceData {
  orderId: string;
  customerName: string;
  phone: string;
  address: string;
  date: string;
  items: InvoiceItem[];
  total: number; // Final total in USD
  subtotal: number; // Subtotal before tax/shipping
  salesTax?: number;
  shippingCost?: number;
  insuranceCost?: number;
  estimatedDuties?: number;
  pkrRate: number;
  type?: 'Invoice' | 'Quotation';
}

export const generateInvoice = async (data: InvoiceData, format: 'pdf' | 'png' | 'jpg' = 'pdf') => {
  const pkrRate = data.pkrRate || 280;
  const docType = data.type || 'Invoice';

  const subtotal = data.subtotal || data.total / 1.16;
  const salesTax = data.salesTax ?? subtotal * 0.18;
  const shippingCost = data.shippingCost || 0;
  const insuranceCost = data.insuranceCost || 0;
  const grandTotal = data.total;

  if (format === 'pdf') {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    
    // Background Pattern
    doc.setFillColor(252, 253, 255); 
    doc.rect(0, 0, pageWidth, pageHeight, 'F');
    
    // Header section
    doc.setFillColor(15, 23, 42); 
    doc.rect(0, 0, pageWidth, 45, 'F');
    
    // Top decorative bar
    doc.setFillColor(37, 99, 235); 
    doc.rect(0, 0, pageWidth, 2, 'F');
    
    // Company Logo / Name
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('HAMIM', 20, 20);
    
    const hamimWidth = doc.getTextWidth('HAMIM');
    doc.setTextColor(239, 68, 68); 
    doc.text('ENTERPRISE', 22 + hamimWidth, 20);
    
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(148, 163, 184); 
    doc.text('SURGICAL INSTRUMENTS MANUFACTURER & EXPORTER', 20, 27);
    
    // Contact Info in Header
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(203, 213, 225);
    doc.text('info@hamimenterprises.com  |  +92 323 5857576', 20, 34);
    doc.text('Sialkot, Pakistan', 20, 39);
    
    // Invoice Title
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text(docType.toUpperCase(), pageWidth - 20, 22, { align: 'right' });
    
    doc.setFontSize(9);
    doc.setTextColor(148, 163, 184);
    const prefix = docType === 'Quotation' ? 'QT' : 'IN';
    const invoiceId = data.orderId.replace('AC', prefix);
    doc.text(`${invoiceId}`, pageWidth - 20, 30, { align: 'right' });
    doc.text(data.date, pageWidth - 20, 36, { align: 'right' });
    
    // Billed To Section
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.1);
    doc.roundedRect(20, 55, pageWidth - 40, 25, 2, 2, 'FD');
    
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(100, 116, 139);
    doc.text('BILL TO', 25, 62);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text(data.customerName, 25, 70);
    
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text(`${data.phone}${data.address ? ' | ' + data.address : ''}`, 25, 75);
    
    // Items Table
    const tableData = data.items.map((item, index) => {
      const pkrPrice = item.price * pkrRate;
      const details = [
        item.brand ? `Brand: ${item.brand}` : null,
        item.origin ? `Origin: ${item.origin}` : null,
        item.condition ? `Cond: ${item.condition}` : null,
        item.size ? `Size: ${item.size}` : null,
        item.usageTime ? `Usage: ${item.usageTime}` : null,
        item.usageLimit ? `Limit: ${item.usageLimit} Ops` : null,
        item.measurement ? `Meas: ${item.measurement}` : null,
        item.length ? `Len: ${item.length}` : null,
        item.quality ? `Qual: ${item.quality}` : null,
        item.material ? `Mat: ${item.material}` : null
      ].filter(Boolean).join(' | ');

      return [
        index + 1,
        `${item.name}${item.code ? ` (${item.code})` : ''}${details ? '\n' + details : ''}${item.description ? '\n' + (item.description.length > 100 ? item.description.substring(0, 97) + '...' : item.description) : ''}`,
        item.quantity.toString(),
        `$${item.price.toFixed(2)}\n(Rs. ${Math.round(pkrPrice).toLocaleString()})`,
        `$${(item.quantity * item.price).toFixed(2)}\n(Rs. ${Math.round(item.quantity * pkrPrice).toLocaleString()})`
      ];
    });

    autoTable(doc, {
      startY: 90,
      margin: { left: 20, right: 20 },
      head: [['#', 'Description & Specifications', 'Qty', 'Unit Price', 'Total']],
      body: tableData,
      theme: 'grid',
      headStyles: { 
        fillColor: [15, 23, 42], 
        textColor: [255, 255, 255], 
        fontSize: 8, 
        fontStyle: 'bold', 
        cellPadding: 3 
      },
      bodyStyles: { 
        fontSize: 7, 
        textColor: [51, 65, 85], 
        cellPadding: 3 
      },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 15, halign: 'center' },
        3: { cellWidth: 40, halign: 'right' },
        4: { cellWidth: 40, halign: 'right' },
      },
      alternateRowStyles: { 
        fillColor: [250, 250, 250] 
      }
    });

    const finalY = (doc as any).lastAutoTable.finalY + 10;

    // Totals Section
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text('Subtotal:', pageWidth - 80, finalY);
    doc.text(`$${subtotal.toFixed(2)}`, pageWidth - 26, finalY, { align: 'right' });

    doc.text('Sales Tax (18%):', pageWidth - 80, finalY + 6);
    doc.text(`$${salesTax.toFixed(2)}`, pageWidth - 26, finalY + 6, { align: 'right' });

    if (shippingCost > 0) {
      doc.text('Shipping:', pageWidth - 80, finalY + 12);
      doc.text(`$${shippingCost.toFixed(2)}`, pageWidth - 26, finalY + 12, { align: 'right' });
    }

    if (insuranceCost > 0) {
      const offset = shippingCost > 0 ? 18 : 12;
      doc.text('Insurance:', pageWidth - 80, finalY + offset);
      doc.text(`$${insuranceCost.toFixed(2)}`, pageWidth - 26, finalY + offset, { align: 'right' });
    }

    const totalOffset = (shippingCost > 0 ? 6 : 0) + (insuranceCost > 0 ? 6 : 0) + 12;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text('Grand Total:', pageWidth - 80, finalY + totalOffset);
    
    doc.setFontSize(14);
    doc.text(`$${grandTotal.toFixed(2)}`, pageWidth - 26, finalY + totalOffset, { align: 'right' });
    
    doc.setFontSize(10);
    doc.setTextColor(220, 38, 38); 
    doc.text(`Rs. ${Math.round(grandTotal * pkrRate).toLocaleString()}`, pageWidth - 26, finalY + totalOffset + 8, { align: 'right' });
    
    doc.setFontSize(6);
    doc.setTextColor(148, 163, 184);
    doc.text(`Rate: 1 USD = ${pkrRate} PKR`, pageWidth - 26, finalY + 28, { align: 'right' });

    // Simple Notes
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(71, 85, 105);
    doc.text('Payment Terms: 50% Advance, 50% on Delivery.', 20, finalY);
    
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(148, 163, 184);
    doc.text('• Prices shown include both USD and PKR for transparency.', 20, finalY + 6);
    doc.text('• Thank you for choosing Hamim Enterprise.', 20, finalY + 11);
    doc.text('• For support, contact us at info@hamimenterprises.com', 20, finalY + 16);
    doc.text('WWW.HAMIMENTERPRISES.COM', 20, finalY + 22);

    const filename = `Invoice_${data.orderId}_${Date.now()}.pdf`;
    doc.save(filename);
  } else {
    // IMAGE GENERATION (PNG/JPG)
    // We create a temporary DOM element, style it identical to invoice, and capture it.
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.top = '-9999px';
    container.style.left = '-9999px';
    container.style.width = '800px';
    container.style.backgroundColor = '#fff';
    container.style.padding = '40px';
    container.style.fontFamily = 'Inter, sans-serif';
    
    const docType = data.type || 'Invoice';
    const prefix = docType === 'Quotation' ? 'QT' : 'IN';
    const invoiceId = data.orderId.replace('AC', prefix);
    
    container.innerHTML = `
      <div style="background: #0f172a; color: #fff; padding: 30px; border-top: 3px solid #2563eb; position: relative;">
        <div style="display: flex; justify-content: space-between; align-items: flex-start;">
          <div>
            <h1 style="font-size: 32px; font-weight: 900; margin: 0;">HAMIM <span style="color: #ef4444;">ENTERPRISE</span></h1>
            <p style="font-size: 10px; font-weight: 700; color: #94a3b8; margin-top: 5px;">SURGICAL INSTRUMENTS MANUFACTURER & EXPORTER</p>
            <p style="font-size: 10px; color: #cbd5e1; margin-top: 10px;">info@hamimenterprises.com | +92 323 5857576</p>
            <p style="font-size: 10px; color: #cbd5e1;">Sialkot, Pakistan</p>
          </div>
          <div style="text-align: right;">
            <h2 style="font-size: 28px; font-weight: 900; margin: 0;">${docType.toUpperCase()}</h2>
            <p style="font-size: 12px; color: #94a3b8; margin-top: 5px;">#${invoiceId}</p>
            <p style="font-size: 12px; color: #94a3b8;">${data.date}</p>
          </div>
        </div>
      </div>
      
      <div style="margin-top: 30px; padding: 20px; border: 1px solid #e2e8f0; border-radius: 8px;">
        <p style="font-size: 10px; font-weight: 900; color: #64748b; margin: 0;">BILL TO</p>
        <h3 style="font-size: 18px; font-weight: 900; color: #0f172a; margin: 5px 0 0 0;">${data.customerName}</h3>
        <p style="font-size: 10px; color: #475569; margin-top: 3px;">${data.phone} ${data.address ? ' | ' + data.address : ''}</p>
      </div>
      
      <table style="width: 100%; border-collapse: collapse; margin-top: 30px;">
        <thead>
          <tr style="background: #0f172a; color: #fff;">
            <th style="padding: 12px; text-align: left; font-size: 11px;">#</th>
            <th style="padding: 12px; text-align: left; font-size: 11px;">Description</th>
            <th style="padding: 12px; text-align: center; font-size: 11px;">Qty</th>
            <th style="padding: 12px; text-align: right; font-size: 11px;">Price (USD/PKR)</th>
            <th style="padding: 12px; text-align: right; font-size: 11px;">Total (USD/PKR)</th>
          </tr>
        </thead>
        <tbody>
          ${data.items.map((item, i) => `
            <tr style="border-bottom: 1px solid #f1f5f9; background: ${i % 2 === 0 ? '#fff' : '#f8fafc'};">
              <td style="padding: 12px; font-size: 11px;">${i + 1}</td>
              <td style="padding: 12px; font-size: 11px; font-weight: 600;">
                <div>${item.name} ${item.code ? `(${item.code})` : ''}</div>
                <div style="font-size: 8px; color: #64748b; font-weight: 400; margin-top: 4px;">
                  ${[
                    item.brand ? `Brand: ${item.brand}` : null,
                    item.origin ? `Origin: ${item.origin}` : null,
                    item.condition ? `Cond: ${item.condition}` : null,
                    item.size ? `Size: ${item.size}` : null,
                    item.usageTime ? `Usage: ${item.usageTime}` : null,
                    item.usageLimit ? `Limit: ${item.usageLimit} Ops` : null,
                    item.measurement ? `Meas: ${item.measurement}` : null,
                    item.length ? `Len: ${item.length}` : null,
                    item.quality ? `Qual: ${item.quality}` : null,
                    item.material ? `Mat: ${item.material}` : null
                  ].filter(Boolean).join(' | ')}
                </div>
                ${item.description ? `<div style="font-size: 8px; color: #64748b; font-weight: 400; margin-top: 2px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; line-height: 1.2; max-height: 2.4em;">${item.description}</div>` : ''}
              </td>
              <td style="padding: 12px; font-size: 11px; text-align: center;">${item.quantity}</td>
              <td style="padding: 12px; font-size: 11px; text-align: right;">
                $${item.price.toFixed(2)}<br/>
                <span style="font-size: 9px; color: #64748b;">Rs. ${Math.round(item.price * pkrRate).toLocaleString()}</span>
              </td>
              <td style="padding: 12px; font-size: 11px; text-align: right;">
                $${(item.quantity * item.price).toFixed(2)}<br/>
                <span style="font-size: 9px; color: #64748b;">Rs. ${Math.round(item.quantity * item.price * pkrRate).toLocaleString()}</span>
              </td>
            </tr>
          `).join('')}
        </tbody>
      </table>
      
      <div style="margin-top: 30px; display: flex; justify-content: space-between; align-items: flex-start;">
        <div>
          <p style="font-size: 11px; font-weight: 900; color: #475569; margin: 0;">Payment Terms: 50% Advance, 50% on Delivery.</p>
          <ul style="font-size: 9px; color: #94a3b8; margin: 8px 0 0 0; padding-left: 15px; list-style-type: disc;">
            <li>Prices shown include both USD and PKR for transparency.</li>
            <li>Thank you for choosing Hamim Enterprise.</li>
            <li>For support, contact us at info@hamimenterprises.com</li>
          </ul>
          <p style="font-size: 10px; color: #94a3b8; margin-top: 10px; font-weight: 700;">WWW.HAMIMENTERPRISES.COM</p>
        </div>
        <div style="text-align: right; padding: 20px; border: 1px solid #e2e8f0; border-radius: 8px; width: 220px;">
          <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
            <span style="font-size: 10px; font-weight: 700; color: #64748b;">Subtotal</span>
            <span style="font-size: 10px; font-weight: 700; color: #0f172a;">$${subtotal.toFixed(2)}</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
            <span style="font-size: 10px; font-weight: 700; color: #64748b;">Tax (18%)</span>
            <span style="font-size: 10px; font-weight: 700; color: #0f172a;">$${salesTax.toFixed(2)}</span>
          </div>
          ${shippingCost > 0 ? `
          <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
            <span style="font-size: 10px; font-weight: 700; color: #64748b;">Shipping</span>
            <span style="font-size: 10px; font-weight: 700; color: #0f172a;">$${shippingCost.toFixed(2)}</span>
          </div>` : ''}
          ${insuranceCost > 0 ? `
          <div style="display: flex; justify-content: space-between; margin-bottom: 10px; padding-bottom: 10px; border-bottom: 1px solid #f1f5f9;">
            <span style="font-size: 10px; font-weight: 700; color: #64748b;">Insurance</span>
            <span style="font-size: 10px; font-weight: 700; color: #0f172a;">$${insuranceCost.toFixed(2)}</span>
          </div>` : '<div style="margin-bottom: 10px; padding-bottom: 10px; border-bottom: 1px solid #f1f5f9;"></div>'}
          <p style="font-size: 11px; font-weight: 900; color: #64748b; margin: 0;">Grand Total</p>
          <p style="font-size: 24px; font-weight: 900; color: #0f172a; margin: 5px 0 0 0;">$${grandTotal.toFixed(2)}</p>
          <p style="font-size: 16px; font-weight: 900; color: #ef4444; margin: 2px 0 0 0;">Rs. ${Math.round(grandTotal * pkrRate).toLocaleString()}</p>
          <p style="font-size: 8px; color: #94a3b8; margin-top: 8px;">Rate: 1 USD = ${pkrRate} PKR</p>
        </div>
      </div>
    `;
    
    document.body.appendChild(container);
    
    try {
      const filename = `Invoice_${data.orderId}_${Date.now()}.${format}`;
      const imageOptions = {
        quality: 0.95,
        skipFonts: true,
        fontEmbedCSS: '',
        cacheBust: true
      };
      const dataUrl = format === 'png' 
        ? await htmlToImage.toPng(container, imageOptions)
        : await htmlToImage.toJpeg(container, imageOptions);
        
      download(dataUrl, filename);
    } catch (error) {
      console.error("Image generation failed:", error);
      alert("Failed to generate image. Please try PDF instead.");
    } finally {
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
    }
  }
};
